<script>
  // New comparison → Common cubes: the comparisons the manifests make
  // possible that no saved one covers yet (GET /api/queries/suggestions), each
  // ticked to start. Adding saves them as ordinary comparisons, which are then
  // edited, deleted and read like any other; the list is read again after, so
  // what was added is gone from it.
  import Section from './Section.svelte';
  import Panel from './Panel.svelte';
  import Button from './Button.svelte';
  import Alert from './Alert.svelte';
  import { getSuggestions, saveQuery } from '../lib/api.js';
  import { VIEWS } from '../../../lib/value_schemes.js';

  let { onadded = () => {} } = $props();

  let data = $state(null);
  let loadError = $state(null);
  let picked = $state({});
  async function load() {
    loadError = null;
    try {
      data = await getSuggestions();
      picked = Object.fromEntries(data.suggestions.map((s) => [s.id, true]));
    } catch (e) { loadError = e.message; }
  }
  load();

  let adding = $state(false);
  let added = $state([]);
  let addError = $state(null);
  const chosen = $derived((data?.suggestions ?? []).filter((s) => picked[s.id]));

  // One at a time: each save rewrites queries.json.
  async function add() {
    adding = true; addError = null; added = [];
    try {
      for (const { schema, ...s } of chosen) added = [...added, await saveQuery(s)];
      onadded();
    } catch (e) { addError = e.message; }
    finally { adding = false; await load(); }
  }
</script>

<Section>
  <Panel>
    {#if loadError}
      <Alert title="Could not read the common cubes" variant="danger"><p class="mono">{loadError}</p></Alert>
    {:else if !data}
      <p class="muted">Reading what you and your partners both share…</p>
    {:else}
      <p class="wf2-text">
        Cubes you and {data.partner} both publish that break down by one and the same
        term, with no comparison saved for them yet. Cubes with the same list of values
        (a survey's agreement items, say) come as one comparison. Once added, they are ordinary comparisons: rename,
        edit or delete them as you like.
      </p>
      {#if added.length}
        <Alert title="Added to your Pod" variant="success">
          <p class="wf2-text">{added.map((q) => `“${q.label}”`).join(', ')}. <a href="#/analysis">See them on Comparisons →</a></p>
        </Alert>
      {/if}
      {#if addError}
        <Alert title="Could not add" variant="danger"><p class="mono">{addError}</p></Alert>
      {/if}
      {#if !data.suggestions.length}
        <p class="muted">
          {data.questions ? 'Every common cube already has a comparison.' : 'You and your partners publish no cube in common yet.'}
        </p>
      {:else}
        <ul class="list">
          {#each data.suggestions as s (s.id)}
            <li>
              <label class="wf2-checkline">
                <input type="checkbox" class="wf2-checkbox" bind:checked={picked[s.id]} />
                <span>
                  <strong>{s.label}</strong>
                  <span class="wf2-caption">Opens as {VIEWS[s.view]}{s.terms ? ` · ${s.terms.length} terms` : ''}</span>
                  {#if s.description}<span class="wf2-caption">{s.description}</span>{/if}
                  {#if s.schema}<span class="wf2-caption">From {s.schema}</span>{/if}
                </span>
              </label>
            </li>
          {/each}
        </ul>
        <div class="wf2-row" style="gap:12px; margin-top: var(--space-2)">
          <Button variant="primary" onclick={add} disabled={adding || !chosen.length}>
            {adding ? `Adding ${added.length + 1} of ${chosen.length}…` : `Add ${chosen.length} comparison${chosen.length === 1 ? '' : 's'}`}
          </Button>
        </div>
      {/if}
    {/if}
  </Panel>
</Section>

<style>
  .list { list-style: none; margin: var(--space-2) 0 0; padding: 0; display: grid; gap: var(--space-2); }
  .list label { align-items: flex-start; }
  .list span { display: block; }
</style>
